/**
 * Compliance entity + category icon system.
 *
 * The full matrix of icon artwork lives in `apps/web/public/icons/`, organised
 * as `icons/<entity>/<category>.svg`. Colour is a property of the *category*,
 * never the entity — the same entity glyph is tinted per category.
 *
 * Treat `category` as colour-only semantics: an entity is a shape, a category
 * is a colour. Today the colour is baked into each pre-rendered file; when we
 * receive clean single-path vectors we recolour a single glyph per entity at
 * runtime from {@link CATEGORY_COLORS}. Nothing downstream should assume "a
 * category maps to a distinct file".
 */

export const ENTITY_TYPES = [
  "checklist",
  "audit",
  "compliance-action",
  "follow-up-action",
  "general-tasks",
  "monitoring",
  "risk-assessment",
  "event",
  "policy-or-procedure",
  "training-induction",
  "supervision",
  "people-case",
] as const;

export type EntityType = (typeof ENTITY_TYPES)[number];

export const CATEGORY_TYPES = [
  "premises-and-environment",
  "activities-and-learning",
  "health-and-safety",
  "fire-safety",
  "emergency-management",
  "food-hygiene-safety-and-nutrition",
  "outings-and-transport",
  "staff-health-safety-and-wellbeing",
  "safeguarding-children",
  "equipment-and-assets",
  "visitors-and-contractors",
  "infection-control",
  "security",
  "send",
  "organisational-governance",
  "coshh",
] as const;

export type CategoryType = (typeof CATEGORY_TYPES)[number];

/**
 * Category colour palette. The source of truth for the eventual runtime
 * recolouring. Hex values from the design system's category/entity spec.
 */
export const CATEGORY_COLORS: Record<CategoryType, string> = {
  "premises-and-environment": "#4A7C59",
  "activities-and-learning": "#1B7895",
  "health-and-safety": "#493979",
  "fire-safety": "#B8627D",
  "emergency-management": "#028090",
  "food-hygiene-safety-and-nutrition": "#1867BE",
  "outings-and-transport": "#1A5276",
  "staff-health-safety-and-wellbeing": "#5C93B2",
  "safeguarding-children": "#8E44AD",
  "equipment-and-assets": "#4A6572",
  "visitors-and-contractors": "#8164D6",
  "infection-control": "#20B2AA",
  security: "#34495E",
  send: "#E2A9F1",
  "organisational-governance": "#3C08D6",
  coshh: "#2C3E50",
};

export const CATEGORY_LABELS: Record<CategoryType, string> = {
  "premises-and-environment": "Premises and Environment",
  "activities-and-learning": "Activities and Learning",
  "health-and-safety": "Health and Safety",
  "fire-safety": "Fire Safety",
  "emergency-management": "Emergency Management",
  "food-hygiene-safety-and-nutrition": "Food Hygiene, Safety & Nutrition",
  "outings-and-transport": "Outings and Transport",
  "staff-health-safety-and-wellbeing": "Staff Health, Safety & Wellbeing",
  "safeguarding-children": "Safeguarding Children",
  "equipment-and-assets": "Equipment and Assets",
  "visitors-and-contractors": "Visitors and Contractors",
  "infection-control": "Infection Control",
  security: "Security",
  send: "SEND",
  "organisational-governance": "Organisational Governance",
  coshh: "COSHH",
};

export const ENTITY_LABELS: Record<EntityType, string> = {
  checklist: "Checklist",
  audit: "Audit",
  "compliance-action": "Compliance Action",
  "follow-up-action": "Follow-up Action",
  "general-tasks": "General Tasks",
  monitoring: "Monitoring",
  "risk-assessment": "Risk Assessment",
  event: "Event",
  "policy-or-procedure": "Policy or Procedure",
  "training-induction": "Training / Induction",
  supervision: "Supervision",
  "people-case": "People Case",
};

/**
 * Page/tab status tones. Colours from the design system's status palette
 * (success-600 / warning-600 / error-500 / neutral grey).
 */
export type StatusTone = "on-track" | "attention" | "action" | "neutral";

export const STATUS_COLORS: Record<StatusTone, string> = {
  "on-track": "#366B59",
  attention: "#CF7945",
  action: "#EF4444",
  neutral: "#D9D9D9",
};

/**
 * Standalone tab/status icons (single-colour artwork in `icons/status/`).
 * Unlike entity icons these are not part of the entity×category matrix.
 */
export const STATUS_ICON_NAMES = [
  "acknowledge-sign-off-approval-grey",
  "alerts-grey",
  "create-task-grey",
  "due-soon-grey",
  "failed-checks",
  "health-allergies-medical-amber",
  "health-allergies-medical-green",
  "health-allergies-medical-grey",
  "health-allergies-medical-red",
  "history-grey",
  "open-grey",
  "recently-completed-grey",
  "templates-grey",
] as const;

export type StatusIconName = (typeof STATUS_ICON_NAMES)[number];
